import { faCalendar, faCircleCheck, faPhone, faTag, faUser, faUsers } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { OrderStatuses, Production } from 'service';
import ArthaGateway from 'service/ArthaGateway';
import { OrderHeader } from './OrderHeader';

type ProductionEntryProps = {
    source: any;
    user: any;
    onSetActiveTab: (tab: string) => void;
    onOrderUpdated: () => void;
};

export const ProductionEntry = ({ source, user, onSetActiveTab, onOrderUpdated }: ProductionEntryProps) => {
    const [productionStage, setProductionStage] = useState<number>(1); // 1 - Pressing, 2 - Packing
    const [order, setOrder] = useState<any>();

    useEffect(() => {
        if (source) {
            setOrder({ ...source });
        }
    }, [source]);

    const saveProduction = () => {
        if (!user || !order) return;

        let production: Production = {
            id: 0,
            date: moment().format("YYYY-MM-DD"),
            saleOrderId: order.id,
            productionTeamId: order.productionPlantId,
            stageId: productionStage,
            items: order.items.map(item => ({
                saleOrderItemId: item.id,
                productId: item.product.id,
                quantity: item.productionQuantity
            })),
            notes: '',
            createdBy: user.id,
            createdAt: moment().format("YYYY-MM-DD")
        };

        // Remove items with zero quantity
        production.items = production.items.filter(i => i.quantity > 0);
        if (production.items.length === 0) {
            toast("Please enter pressed/packed quantities.", { position: 'bottom-center', className: 'btn bg-warning text-white' });
            return;
        }

        ArthaGateway.saveProduction(production, user?.id || 0)
            .then((data) => {
                toast("Production added successfully.", { position: 'bottom-center', className: 'btn bg-success text-white' });
                onOrderUpdated();
            })
            .catch((error) => {
                console.log(error);
                toast("Failed to add production.", { position: 'bottom-center', className: 'btn bg-warning text-white' });
            });
    }

    const updateProductionQuantity = (index: number, qty: number) => {
        if (!order) return;
        setOrder(prevOrder => ({
            ...prevOrder,
            items: prevOrder.items.map((item, idx) =>
                idx === index ? { ...item, productionQuantity: qty } : item
            )
        }));
    }

    return (
        <>
            {order && (
                <div className='card'>
                    <div className="card-header">
                        <h3 className="order-id">Order ID: <b>{order.voucherNumber}</b></h3>
                    </div>
                    <OrderHeader order={order} user={user} />
                    <div className='card-body p-0'>
                        <select className="form-select" value={productionStage} onChange={(e) => setProductionStage(Number(e.target.value))}>
                            <option value="1">Pressing</option>
                            <option value="2">Packing</option>
                        </select>
                    </div>
                    <div className='card-body p-0'>
                        <table className="table table-bordered bg-white mb-2">
                            <thead>
                                <tr>
                                    <th className="w-33">Order Qty.</th>
                                    <th className="w-33">
                                        {productionStage === 1 && 'Already Pressed'}
                                        {productionStage === 2 && 'Already Packed'}
                                    </th>
                                    <th>
                                        {productionStage === 1 && 'Pressed Qty'}
                                        {productionStage === 2 && 'Packed Qty'}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {order.items.map((item, index) => (
                                    <>
                                        <tr key={index}>
                                            <td colSpan={3}>
                                                <b>{item.productName}</b>
                                                {item.styleName && <span> (Style: {item.styleName})</span>}
                                                {item.narration && <span> ({item.narration || '-'})</span>}
                                            </td>
                                        </tr>
                                        <tr key={index + "-qty"}>
                                            <td>{item.quantity || 0}</td>
                                            {productionStage === 1 && (
                                                <>
                                                    <td>{item.pressedQuantity || 0}</td>
                                                    <td className="p-0">
                                                        <select className="form-select" value={item.productionQuantity} onChange={(e) => updateProductionQuantity(index, Number(e.target.value))}>
                                                            {Array.from({ length: (item.quantity - (item.pressedQuantity || 0)) + 1 }, (_, i) => i).map((qty) => (
                                                                <option key={qty} value={qty}>{qty}</option>
                                                            ))}
                                                        </select>
                                                    </td>
                                                </>
                                            )}
                                            {productionStage === 2 && (
                                                <>
                                                    <td>{item.packedQuantity || 0}</td>
                                                    <td className="p-0">
                                                        <select className="form-select" value={item.productionQuantity} onChange={(e) => updateProductionQuantity(index, Number(e.target.value))}>
                                                            {Array.from({ length: ((item.pressedQuantity || 0) - (item.packedQuantity || 0)) + 1 }, (_, i) => i).map((qty) => (
                                                                <option key={qty} value={qty}>{qty}</option>
                                                            ))}
                                                        </select>
                                                    </td>
                                                </>
                                            )}
                                        </tr>
                                    </>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="card-footer p-2">
                        <div className="btn btn-success w-100 mb-2" onClick={saveProduction}>
                            Save Production
                        </div>
                        <div className="btn btn-sm btn-danger w-100" onClick={() => { onSetActiveTab('details'); }}>
                            Cancel
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}